import { far, fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Box,
  Card,
  CardActionArea,
  CardHeader,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDialog } from '../dialog/DialogContext';
import { ConfirmDeleteDialog } from '../entityFramework/entityList/ConfirmDeleteDialog';
import { useNavState } from '../navState/NavStateContext';
import { usePrincipal } from '../principal/PrincipalContext';
import { useRsrc } from '../rsrc/RsrcContext';
import { ExprUtils } from '../utils/ExprUtils';
import { BookmarkDialog } from './BookmarkDialog';

export const BookmarkMenu = ({ anchorEl, open, onClose }) => {
  const rsrc = useRsrc();
  const { principal } = usePrincipal();
  const { openDialog } = useDialog();
  const { navState, navigateTo } = useNavState();
  const [bookmarks, setBookmarks] = useState([]);

  const loadBookmarks = () => {
    rsrc.rows('admin', 'BOOKMARK', { expr: ExprUtils.whereParams('username', principal.username) }).then((res) => {
      setBookmarks(res);
    });
  };

  useEffect(() => {
    if (open) {
      loadBookmarks();
    }
  }, [open]);

  const openBookmarkDialog = (bookmark) => {
    const data = { username: principal.username, navState, bookmark };
    openDialog(BookmarkDialog, { data }).then((saved) => {
      if (saved) loadBookmarks();
    });
  };

  const toBookmark = (bookmark) => {
    onClose();
    const navState = bookmark.navState ? JSON.parse(bookmark.navState) : {};
    navigateTo(bookmark.url, navState);
  };

  const deleteBookmark = (bookmark) => {
    const data = { module: 'admin', entity: 'BOOKMARK', id: bookmark.id };
    openDialog(ConfirmDeleteDialog, { data }).then(() => loadBookmarks());
  };

  const renderMenuContent = () => {
    // No Bookmarks Scenario
    if (bookmarks.length === 0) {
      return (
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No bookmarks
          </Typography>
        </Box>
      );
    }

    // Render Content for Bookmarks
    return (
      <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
        {bookmarks.map((bookmark, index) => (
          <Box key={bookmark.id} sx={{ p: 1 }}>
            <BookmarkRow
              bookmark={bookmark}
              index={index}
              onOpen={toBookmark}
              onEdit={openBookmarkDialog}
              onDelete={deleteBookmark}
            />
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      transformOrigin={{ vertical: 'center', horizontal: 'left' }}
    >
      <Box sx={{ width: 320 }}>
        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ px: 1 }}>
          <Tooltip title="Bookmark this Page">
            <IconButton size="small" onClick={() => openBookmarkDialog()} data-test-id="bookmark-add-button">
              <FontAwesomeIcon icon={fas.faPlus} size="xs" />
            </IconButton>
          </Tooltip>
        </Stack>
        {renderMenuContent()}
      </Box>
    </Menu>
  );
};

const BookmarkRow = ({ bookmark, index, onOpen, onEdit, onDelete }) => {
  const [menuAnchor, setMenuAnchor] = useState(null);

  const openMenu = (event) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  const handleEdit = () => {
    closeMenu();
    onEdit(bookmark);
  };

  const handleDelete = () => {
    closeMenu();
    onDelete(bookmark);
  };

  return (
    <Card variant="outlined">
      <Stack direction="row" alignItems="center">
        <CardActionArea onClick={() => onOpen(bookmark)}>
          <CardHeader data-test-id={`bookmark-item-${index}`} title={bookmark.name} subheader={bookmark.url} />
        </CardActionArea>
        <IconButton onClick={openMenu} data-test-id={`bookmark-actions-${index}`} sx={{ mr: 1 }}>
          <FontAwesomeIcon icon={fas.faEllipsisVertical} size="xs" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          <MenuItem onClick={handleEdit} data-test-id={`bookmark-edit-${index}`}>
            <ListItemIcon>
              <FontAwesomeIcon icon={far.faPencil} size="xs" />
            </ListItemIcon>
            <ListItemText>Edit this bookmark</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleDelete} data-test-id={`bookmark-delete-${index}`}>
            <ListItemIcon>
              <FontAwesomeIcon icon={fas.faTrash} size="xs" />
            </ListItemIcon>
            <ListItemText>Delete this bookmark</ListItemText>
          </MenuItem>
        </Menu>
      </Stack>
    </Card>
  );
};
